
import {defineComponent} from 'vue'
import ValueFormatter from '@/utils/value-formatter'
import {ReportChart, ReportChartAxis, ReportChartOption, ReportChartType, ReportData} from '@/model/models'
import ReportLineChart from '@/components/ReportLineChart.vue'
import ReportBarChart from '@/components/ReportBarChart.vue'
import ReportPieChart from '@/components/ReportPieChart.vue'
import ReportBubbleChart from '@/components/ReportBubbleChart.vue'

export default defineComponent( {
  name: 'ReportChartView',
  components: {
    ReportLineChart,
    ReportBarChart,
    ReportPieChart,
    ReportBubbleChart
  },
  props: {
    chart: ReportChart,
    data: ReportData,
  },
  data () {
    return {
      colors: ['#CD6155', '#7DCEA0', '#5499C7', '#F7DC6F', '#AF7AC5', '#E59866', '#F1948A', '#76D7C4', '#AED6F1', '#FAD7A0', '#D2B4DE', '#FAD7A0']
    }
  },
  methods: {
    axisValues(axis: ReportChartAxis, minPercentage = 0.0) : string[] {
      if (this.data) {
        const index = this.data.columns.indexOf(axis.value)
        const values = this.data.values.map(v => v[index])

        if (minPercentage > 0.0 && ValueFormatter.isNumberFormat(axis.format)) {
          const total = values.reduce((t,v) => t + v)
          var minValue = total * (minPercentage / 100.0)
          var newValues = values.filter(v => v >= minValue)

          if ((values.length - newValues.length) > 1) {
            const other = values.filter(v => v < minValue).reduce((t,v) => t + v)
            return newValues.concat([other]).map(v => ValueFormatter.format(v, axis.format))
          }
        }

        return values.map(v => ValueFormatter.format(v, axis.format))
      }

      return []
    }
  },
  computed: {
    isLineChart() {
      return this.chart?.type === ReportChartType.Line
    },
    isBarChart() {
      return this.chart?.type === ReportChartType.Bar
    },
    isPieChart() {
      return this.chart?.type === ReportChartType.Pie
    },
    isBubbleChart() {
      return this.chart?.type === ReportChartType.Bubble
    },
    chartData() {
      if (this.chart) {
        const fixedColor = this.chart.options.indexOf(ReportChartOption.FixedColor) >= 0
        const series = this.chart.series.map(s => this.axisValues(s, this.chart?.minimum))
        const valueCount = Math.min(...series.map(s => s.length))
        var labels = this.axisValues(this.chart!.axis)

        if (valueCount < labels.length) {
          labels = labels.slice(0, valueCount - 1).concat(['Other'])
        }

        switch (this.chart.type) {
          case ReportChartType.Line:
            return { labels: labels, datasets: series.map((s, idx) => {
                const colors = fixedColor ? this.colors[idx] : this.colors
                return { label: this.chart!.series[idx].title, data: s, backgroundColor: colors, borderColor: colors }
              })
            }

          case ReportChartType.Bar:
            return { labels: labels, datasets: series.map((s, idx) => {
                return { label: this.chart!.series[idx].title, data: s, backgroundColor: fixedColor ? this.colors[idx] : this.colors }
              })
            }

          case ReportChartType.Pie:
            return { labels: labels, datasets: series.map((s, idx) => {
                const colors = fixedColor ? this.colors[idx] : this.colors

                return {
                  data: s,
                  datalabels: {
                    formatter: (value: any, context: { chart: { data: { labels: { [x: string]: any } } }; dataIndex: number }) => {
                      return context.chart.data.labels[context.dataIndex]
                    }
                  },
                  backgroundColor: colors,
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                  borderWidth: 2
                }
              })
            }
        }
      }

      return { labels: [], datasets: [] }
    },
    bubbleChartData() {
      if (this.chart) {
        var data = this.data!.values.map((s, idx) => {
          return { x: idx, y: s[2], r: s[1]  }
        })

        return { datasets: { data: data, backgroundColor: this.colors } }
      }

      return { datasets: [] }
    }
  }
})
